import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** DESIGN.md §9.1 `Portal`. Renders children into `document.body` — the only sane way for a
 * `Dialog`/`Menu`/`Toast` to escape an `overflow: hidden` ancestor (operate.md's "Overlays escape
 * their container" rule). SSR-safe no-op guard is unnecessary here (this app has no SSR).
 *
 * P1 fix (round 5): this used to create its OWN wrapper `<div>` and only attach it to
 * `document.body` from a `useEffect` — a passive effect, which fires only after React's whole
 * commit (including any child's `autoFocus`, e.g. `SingerPicker`'s name field) has already run.
 * `autoFocus`'s one-time `.focus()` call happens while that wrapper is still a detached node with
 * no browsing context, where focus is a silent no-op — confirmed live: `document.activeElement`
 * stayed `<body>` through the whole dialog-open commit, and under StrictMode's dev-only
 * mount→cleanup→mount replay of that same effect, the wrapper's `removeChild` mid-replay could
 * blur focus to `null` with nothing left to re-claim it. Targeting `document.body` directly has no
 * such window: it is always attached, so whatever this portals in is connected to the live
 * document the instant React inserts it, in the same commit `autoFocus` runs in. React already
 * removes exactly the nodes it inserted here on unmount — no wrapper bookkeeping needed. */
export function Portal({ children }: { children: ReactNode }) {
  return createPortal(children, document.body);
}
